import { Component, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RequestOtpComponent } from './pages/request-otp/request-otp.component';
import { SubmitOtpComponent } from './pages/submit-otp/submit-otp.component';
import { RegisterEmailComponent } from './pages/register-email/register-email.component';
import { UserRegistration } from './pages/user-profile/model/model.userProfileForm';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { serviceOptionsState } from 'src/app/store/service-options';
import { CmsCountryObj, LanguageConfig } from 'src/app/app.models';
import { getCurrentLabelsJson } from 'src/app/store/labels';
import { countriesInfo } from 'src/app/store/countries';
import { FirebaseService } from 'src/app/services/firebase.service';
import { Auth } from '@angular/fire/auth';
import { RecaptchaVerifier } from 'firebase/auth';
import { WindowService } from 'src/app/services/window.service';
import { EnvService } from 'src/app/services/env.service';
import { ErrorService } from 'src/app/services/error.service';
import { UserPhoneDetails } from 'src/app/models/sign-in.models';
import { LoadingService } from 'src/app/services/loading.service';
import { CheckDigitalProfile, CheckDigitalProfileFound, CheckDigitalProfileNotFound, CreateProfileProductTypeSuccess, GetPhoneByWalletIdFound, GetPhoneByWalletIdNotFound, RegisterNewProfile, SetEmailAddress, SetFirebaseAuthToken, SetWalletId, UpdateProfileProductType, UpdateProfileProductTypeSuccess } from 'src/app/store/auth/auth.actions';
import { selectAuthState } from 'src/app/store/auth/auth.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { RiskStatus } from 'src/app/store/auth/auth.model';


interface DecodedJWT {
  client_domain: string
  exp: number
  iat: number 
  iss: string 
  jti: string
  sub: string
  client_name?: string
}

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent {
  @ViewChild('requestOtpComponent') public requestOtpComponent: RequestOtpComponent;
  @ViewChild('submitOtpComponent') public submitOtpComponent: SubmitOtpComponent;
  @ViewChild('registerEmailComponent') public registerEmailComponent: RegisterEmailComponent;

  public flow: 'login' | 'register' | null | string = null; // TODO: Tim: fix typing
  public steps: { [id: string]: number } = {
    signInLanding: 0,
    requestOtp: 1,
    submitOtp: 2,
    registerEmail: 3,
    userProfile: 4,
    riskSdk: 5
  };

  private jwtToken: string;

  public showFlashPage: boolean = false;
  public languageList: LanguageConfig[] = [];
  public serviceOptionsCmsMappingCountries: CmsCountryObj;
  private userWalletId: string = '';
  public userDialCode: string;
  public userPhoneIso2Code: string;
  public userPhoneNumber: string;
  public userInternationPhoneNumber: string = '';
  public userEmail: string = '';
  private userDigitalProfileId: string = '';
  private userAuthToken: string = '';
  private hasExistingProfile: boolean | null;
  private isWalletRecognized: boolean | null;
  private otpConfirmationResult: any = null;
  public cmsLabels: any; // TODO: Tim: Fix types
  public riskStatus: RiskStatus | null;

  public cmsLabels$: Observable<any>; // TODO: Tim: Fix types
  public hasError$: Observable<boolean> | undefined; // TODO: tim: is this used??
  public isServerDown$: Observable<boolean> | undefined; // TODO: tim: is this used??
  public countries$:  Observable<any[]> | undefined;
  public isStatesResolved$: Observable<boolean> | undefined;
  
  private serviceOptionsStateSub: Subscription;
  private cmsLabelsStateSub: Subscription;
  private authStateSub: Subscription;
  private getPhoneByWalletIdNotFoundSub: Subscription;
  private getPhoneByWalletIdFoundSub: Subscription;
  private checkDigitalProfileSub: Subscription;
  private setFirebaseAuthTokenSub: Subscription;
  private updateProfileProductTypeSuccessSub: Subscription;
  private createProfileProductTypeSuccessSub: Subscription;

  private windowRef: any; // Todo: Tim: fix type
  public isRecaptchaSolved: boolean = false; // TODO: tim: is this used??
  private recaptchaToken: string; // TODO: tim: is this used??
  public isSignInEnabled: boolean = false;
  private isFirebaseEnabled: boolean = false;
  public isRegressionEnv: boolean = false;
  public resetOtpTimerInSec: number;

  private auth = inject(Auth);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<any>,
    private firebaseService: FirebaseService,
    private envService: EnvService,
    private win: WindowService,
    private errorService: ErrorService,
    private loadingService: LoadingService,
    private actions$: Actions
  ) {
  }

  public currentStep: any = 0;

  ngOnInit() {
    this.authStateSub = this.store.select(selectAuthState).subscribe(authState => {
      console.log('Auth state is updating', authState); // Todo: remove: #may6
      this.userWalletId = authState.walletId
      this.userDialCode = authState.userProfile.dialCode
      this.userPhoneNumber = authState.userProfile.phoneNumber
      this.userInternationPhoneNumber = authState.userProfile.internationalPhoneNumber
      this.userEmail = authState.userProfile.email
      this.userDigitalProfileId = authState.digitalProfileId
      this.userAuthToken = authState.firebaseAuthToken
      this.hasExistingProfile = authState.hasExistingProfile
      this.isWalletRecognized = authState.isWalletRecognized
      this.riskStatus = authState.riskStatus
    })

    this.serviceOptionsStateSub = this.store.select(serviceOptionsState).subscribe((serviceOptions) => {
      if (serviceOptions.config) {
        this.isSignInEnabled = serviceOptions.config?.isSignInEnabled ?? false;
        this.isFirebaseEnabled = serviceOptions.config?.isFirebaseEnabled ?? false;
        if (this.isSignInEnabled === false) {
          this.navigateToTransactionFlow();          
        }
      } else {
        this.navigateToTransactionFlow(); // Todo: Tim: #march25 - double check this functionality
      }
      this.resetOtpTimerInSec = serviceOptions?.config?.resetOtpTimerInSec ?? 0;
      this.languageList = serviceOptions?.uiConfigs[0]?.languageList || [];
      this.showFlashPage = serviceOptions?.uiConfigs[0]?.displayFlashPage || false;
    })

    this.countries$ = this.store.select(countriesInfo);

    this.cmsLabels$ = this.store.select(getCurrentLabelsJson);
    this.cmsLabelsStateSub = this.cmsLabels$.subscribe((labels) => {
      if (labels?.labels?.stellar ?? false) {
        this.cmsLabels = labels.labels;
      }
      else {
        this.navigateToTransactionFlow();
      }
    })

    this.route.queryParams.subscribe((params: any) => { // Todo: Tim: Fix type
      this.jwtToken = params['token'];

      // DecodeJWT to get walletID
      if (this.jwtToken) {
        this.store.dispatch(SetWalletId({ walletId: this.getWalletIDFromJWT(this.jwtToken)}))
      }
      this.isRegressionEnv = this.envService.getEnv().toLowerCase() === 'q1' ? true : false;
    })    

    // Listen to NGRX Actions
    this.getPhoneByWalletIdNotFoundSub = this.actions$.pipe(
      ofType(GetPhoneByWalletIdNotFound)
    ).subscribe(async (action) => {
      this.flow = 'register';
      this.currentStep = this.steps['requestOtp'];
    })

    this.getPhoneByWalletIdFoundSub = this.actions$.pipe(
      ofType(GetPhoneByWalletIdFound)
    ).subscribe(async (action) => {
      this.flow = 'login';
      await this.requestOtp(this.userInternationPhoneNumber);
    })

    this.checkDigitalProfileSub = this.actions$.pipe(
      ofType(
        CheckDigitalProfileFound,
        CheckDigitalProfileNotFound
      )
    ).subscribe(async (action) => {
      this.flow = action?.digitalProfileId ? 'login' : 'register';
      await this.requestOtp(this.userInternationPhoneNumber);
    })

    this.setFirebaseAuthTokenSub = this.actions$.pipe(
      ofType(
        SetFirebaseAuthToken
      )
    ).subscribe((action) => {
      console.log('action', action) // Todo: remove: #may6
      if (this.flow === 'register') {
        this.currentStep = this.steps['registerEmail'];
      }
      else if (this.flow === 'login' && this.isWalletRecognized) {
        if (this.isUserVerificationDeclined(this.riskStatus)) {
          // Todo: Tim: #may6 - revisit this. How should UI handle DECLINE status??
          this.errorService.open('User verification is rejected', this.cmsLabels.stellar.dismiss)
          this.navigateToTransactionFlow()
          return
        }
        else if (this.isIdVerificationRequired(this.riskStatus)) {
          this.currentStep = this.steps['riskSdk']
        }
        else {
          this.navigateToDigitalPaymentFlow();
        }
      }
      else if (this.flow === 'login' && !this.isWalletRecognized) {
        if (this.isUserVerificationDeclined(this.riskStatus)) {
          // Todo: Tim: #may6 - revisit this. How should UI handle DECLINE status??
          this.navigateToTransactionFlow()
          return
        }
        else {
          this.store.dispatch(UpdateProfileProductType({dpid: this.userDigitalProfileId, walletId: this.userWalletId}));
        }
      }
    })

    this.updateProfileProductTypeSuccessSub = this.actions$.pipe(
      ofType(
        UpdateProfileProductTypeSuccess
      )
    ).subscribe((action) => {
      if (this.isIdVerificationRequired(this.riskStatus)) {
        this.currentStep = this.steps['riskSdk']
      }
      else {
        this.navigateToDigitalPaymentFlow();
      }
    })

    // Todo: Tim: this may be redundant with the above subscription. Consolidate these if there are no additional behaviour changes between the 2
    this.createProfileProductTypeSuccessSub = this.actions$.pipe(
      ofType(
        CreateProfileProductTypeSuccess
      )
    ).subscribe((action) => {
      if (this.isIdVerificationRequired(this.riskStatus)) {
        this.currentStep = this.steps['riskSdk']
      }
      else {
        this.navigateToDigitalPaymentFlow();
      }
    })
  }

  ngOnDestroy() {
    if (this.authStateSub) { this.authStateSub.unsubscribe(); }
    if (this.serviceOptionsStateSub) { this.serviceOptionsStateSub.unsubscribe(); }
    if (this.cmsLabelsStateSub) { this.cmsLabelsStateSub.unsubscribe(); }
    if (this.getPhoneByWalletIdNotFoundSub) { this.getPhoneByWalletIdNotFoundSub.unsubscribe(); }
    if (this.getPhoneByWalletIdFoundSub) { this.getPhoneByWalletIdFoundSub.unsubscribe()}
    if (this.checkDigitalProfileSub) { this.checkDigitalProfileSub.unsubscribe()}
    if (this.setFirebaseAuthTokenSub) { this.setFirebaseAuthTokenSub.unsubscribe()}
    if (this.updateProfileProductTypeSuccessSub) { this.updateProfileProductTypeSuccessSub.unsubscribe()}
    if (this.createProfileProductTypeSuccessSub) { this.createProfileProductTypeSuccessSub.unsubscribe()}
  }

  private async initializeRecaptcha(): Promise<void> { // Todo: Tim: #mar25 - why is this async??
    console.log('initializeRecaptcha start'); // Todo: remove: #mar25
    this.loadingService.isLoading.next(true);
    this.windowRef = this.win.windowRef;
    this.windowRef.recaptchaVerifier = new RecaptchaVerifier(this.auth, 'recaptcha-container', {
      'size': 'invisible',
      'callback': (response: any) => {
        console.log('RECAPTCHA IS SOLVED', response); // TODO: remove: #mar25
        this.isRecaptchaSolved = true;
      },
      // 'expired-callback': () => { // Todo: Tim: Turn on if a visible recaptcha is used
      //   // Response expired. Ask user to solve reCAPTCHA again.
      //   // Todo: Tim: add logic to reset recaptcha
      //   this.isRecaptchaSolved = false;
      // }
    }); // Todo: Tim: #recaptcha - for q1 regression, should this not initialize with an auth instance?
    this.loadingService.isLoading.next(false);
    console.log('initializeRecaptcha end'); // Todo: remove: #mar25
  }

  public updateSignInFlow(flowType: string): void {
    this.flow = flowType;
  }

  public navigateToTransactionFlow(): void {
    this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
  }

  public navigateToDigitalPaymentFlow(): void {
    this.router.navigate(['/digital-payment'], { queryParamsHandling: 'preserve' });
  }

  public handleRequestOTPEvent(userPhoneDetails: UserPhoneDetails): void {
    this.store.dispatch(CheckDigitalProfile(userPhoneDetails));
  }

  private async requestOtp(intlPhone: string): Promise<void> {
    // Todo: Tim: #Q1 regression. We should not use Firebase in Q1
    if (!this.windowRef?.recaptchaVerifier && !this.isRegressionEnv && this.isFirebaseEnabled) {
      console.log('requestOtp - initializeRecaptcha START'); // Todo: remove: #mar25
      await this.initializeRecaptcha();
      console.log('requestOtp - initializeRecaptcha COMPLETE'); // Todo: remove: #mar25
    }
    if (!this.isRegressionEnv && this.isFirebaseEnabled) {
      this.loadingService.isLoading.next(true)
      console.log('requestOtp - VERIFY Recaptcha START'); // Todo: remove: #mar25
      this.recaptchaToken = await this.windowRef.recaptchaVerifier.verify();
      console.log('requestOtp - VERIFY Recaptcha COMPLETE'); // Todo: remove: #mar25
      this.loadingService.isLoading.next(false)
      console.log('recaptchaToken', this.recaptchaToken); // Todo: remove: #mar25
    }
    // Todo: Tim: revist this block to make this HTTP request via NGRX effects
    try {
      // For q1 regression, we do not want to actually call the real firebase service. We will return a mocked response instead
      if (this.isRegressionEnv || !this.isFirebaseEnabled) {
        this.otpConfirmationResult = await this.firebaseService.loginWithPhone(intlPhone, {}, true);
      }
      else {
        this.otpConfirmationResult = await this.firebaseService.loginWithPhone(intlPhone, this.windowRef.recaptchaVerifier, false); // Todo: Tim: #mar25 - revert
      }
    } catch (error) {
      console.error('error during loginWithPhone:', error);
    } finally {
      if (!!this.otpConfirmationResult) {
      this.currentStep = this.steps['submitOtp'];

      }
      // Todo: Tim: #mar25 Should display a user error if OTP confirm result is not returned. Possibly reset OTP Form
    }
  }

  public async resendOtp(): Promise<void> {
    try {
      if (this.isRegressionEnv || !this.isFirebaseEnabled) {
        // console.log('in resendOtp... triggering login again MOCK'); // Todo: remove: #mar25
        this.otpConfirmationResult = await this.firebaseService.loginWithPhone(this.userInternationPhoneNumber, {}, true);
      }
      else {
        // console.log('in resendOtp... triggering login again'); // Todo: remove: #mar25
        this.otpConfirmationResult = await this.firebaseService.loginWithPhone(this.userInternationPhoneNumber, this.windowRef.recaptchaVerifier, false);
      }
    } catch (error) {
      console.error('error during resendOtp:', error);
      this.errorService.open(this.cmsLabels.stellar.error.somethingHasGoneWrong, this.cmsLabels.stellar.dismiss); // Todo: #CMS
    }
  }

  public async verifyOtp(otp: string) {
    if (!otp) {
      console.error('otp is null/invalid');
    }
    try { // Todo: Tim: should refactor into the firebase.service
      const otpResult = await this.otpConfirmationResult.confirm(otp);
      const firebaseAuthToken = await this.auth.currentUser?.getIdToken() ?? 'notoken'; // Todo: tim:
      this.store.dispatch(SetFirebaseAuthToken({ firebaseAuthToken }))
    } catch (error) {
      console.error('error during verifyOtp:', error);
      this.errorService.open(this.cmsLabels.stellar.error.somethingHasGoneWrong, this.cmsLabels.stellar.dismiss); // Todo: #CMS
    }
  }

  public handleSubmitEmailEvent(emailAddress: string): void {
      this.store.dispatch(SetEmailAddress({ email: emailAddress}));
      this.currentStep = this.steps['userProfile'];
      
  }

  public handleRiskSdkCompleteEvent(): void {
    // Todo: Tim: #may6 - Redirect user to appropriate page ("sendvalidate" or "not approved");
    this.navigateToDigitalPaymentFlow()
  }

  private resetAllChildForms() {
    this.requestOtpComponent.resetPhoneForm();
    this.submitOtpComponent.resetOtpForm();
    this.registerEmailComponent.resetEmailForm();
  }

  handleBackButton(): void {
    this.resetAllChildForms(); // Todo: Tim: #mar25 - may not be necessary if the components/forms are destroyed when we switch routes
    this.navigateToTransactionFlow();
  }

  public displayBackButton() {
    if (!!this.currentStep && this.currentStep < this.steps['registerEmail']) return true;
    return false; // Todo: Tim: #mar25
  }

  // Todo: decodeJWT and isClientNameInJWT are pure functions, used in both home.ts and sign-in.ts. These can be refactored into a utils file. 
  public decodeJWT(token: string): DecodedJWT {
    const arrayToken = token.split('.');
    const decodedToken = JSON.parse(atob(arrayToken[1]));
    return decodedToken;
  }

  public isClientNameInJWT(token: string): boolean {
    const decodedToken = this.decodeJWT(token);
    return !!decodedToken['client_name'];
  }

  private getWalletIDFromJWT(token: string): string {
    const decodedToken: DecodedJWT = this.decodeJWT(token);
    const walletId: string = decodedToken.sub;
    return walletId;
  }
  
  public async onNotifyUserRegistration(userRegistration: UserRegistration): Promise<void> {
    // console.log('userRegistration submitted value', userRegistration); // Todo: remove: #mar25
    const payload = {
      userRegistration: userRegistration,
      walletId: this.userWalletId,
      email: this.userEmail,
      phoneNumber: this.userPhoneNumber,
      dialCode: this.userDialCode,
      fbAuthToken: this.userAuthToken
    }
    this.store.dispatch(RegisterNewProfile(payload))
  }

  private isUserVerificationDeclined(riskStatus: RiskStatus | null): boolean {
    // console.log('riskStatus', riskStatus) // todo: remove: #may6
    if (riskStatus === null) return true
    const isDeclined = riskStatus.isRedirectRequired
    if (isDeclined) {
      return true
    }
    else {
      return false
    }
  }

  private isIdVerificationRequired(riskStatus: RiskStatus | null): boolean {
    if (riskStatus === null) return true
    return riskStatus?.isStepUpRequired ?? true;
  }
}
